import React from "react";
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ProjectUtilisationSummary from "./ProjectUtilisationSummary";
import { SectionLabel, UtilisationStat } from "./UtilisationParts";
import { useUtilisationColors } from "./useUtilisationColors";

const NON_FIXED_NOTE = "Budget progress is tracked for Fixed Cost projects only.";

const pluralise = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// One project's block when several projects were saved together.
const ProjectCard = ({ project, onViewBudget }) => {
  const colors = useUtilisationColors();

  return (
    <Box
      data-testid="project-utilisation-card"
      sx={{ border: `1px solid ${colors.line}`, borderRadius: "12px", p: 1.5, display: "flex", flexDirection: "column", gap: 1.1 }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.ink, minWidth: 0 }}>
          {project.projectName}
        </Typography>
        <Typography sx={{ fontSize: 12, color: colors.ink2, whiteSpace: "nowrap" }}>
          {pluralise(project.logCount, "log")} · {project.durationLabel}
        </Typography>
      </Box>

      {project.isFixedCost ? (
        <ProjectUtilisationSummary
          variant="compact"
          hoursLabel={project.hoursLabel}
          individualPct={project.individualPct}
          projectPct={project.projectPct}
          targetPercent={project.targetPercent}
          targetUsedPct={project.targetUsedPct}
          onViewBudget={onViewBudget ? () => onViewBudget(project.projectId) : undefined}
        />
      ) : (
        <HoursOnly project={project} compact />
      )}
    </Box>
  );
};

// Non-fixed-cost projects have no budget to measure, so the popup shows hours only.
const HoursOnly = ({ project, compact = false }) => {
  const colors = useUtilisationColors();
  return (
    <Box data-testid="hours-only" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {!compact && <SectionLabel>Your contribution</SectionLabel>}
      <UtilisationStat compact={compact} outlined={!compact} label="Hours by you" value={project.hoursLabel} />
      <Typography sx={{ fontSize: 12, color: colors.ink3 }}>{NON_FIXED_NOTE}</Typography>
    </Box>
  );
};

/**
 * Shown after one or more time logs are saved. Built on MUI Dialog like the other
 * confirmation dialogs in the app.
 *
 * One project  -> the full breakdown for that project.
 * Several      -> a single popup with one compact card per project (never one popup per log).
 * Not fixed cost -> hours only, no budget card.
 *
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {Function} props.onClose
 * @param {Function} [props.onSkip]          - defaults to onClose
 * @param {Function} [props.onAddAnother]    - hides the button when omitted
 * @param {Function} [props.onViewBudget]    - called with the projectId
 * @param {object}   props.summary           - { date, logCount, durationLabel, totalDurationLabel, typeLabel, billingLabel }
 * @param {Array}    props.projects          - one entry per project in the batch
 */
const TimeLogSavedDialog = ({ open, onClose, onSkip, onAddAnother, onViewBudget, summary, projects = [] }) => {
  const colors = useUtilisationColors();
  const logCount = summary?.logCount ?? 1;
  const isBatch = projects.length > 1;
  const single = projects[0];

  const title = logCount > 1 ? `${logCount} time logs saved` : "Time log saved";
  const totalLabel = summary?.totalDurationLabel ? `${summary.totalDurationLabel} total` : null;
  const subtitle = isBatch
    ? [summary?.date, totalLabel, pluralise(projects.length, "project")]
    : logCount > 1
      ? [summary?.date, totalLabel]
      : [summary?.date, summary?.durationLabel, summary?.typeLabel, summary?.billingLabel];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      scroll="paper"
      aria-labelledby="time-log-saved-title"
      PaperProps={{ sx: { borderRadius: "16px", bgcolor: colors.paper, color: colors.ink, m: 2, width: "100%" } }}
    >
      <DialogContent sx={{ p: 2.25, display: "flex", flexDirection: "column", gap: 1.6 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "50%",
              background: colors.tealDeep,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography id="time-log-saved-title" sx={{ fontSize: 18, fontWeight: 800, color: colors.ink }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 13, color: colors.ink2 }}>{subtitle.filter(Boolean).join(" · ")}</Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} sx={{ width: 44, height: 44, mt: -1, mr: -1.25, color: colors.ink2 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {isBatch ? (
          projects.map((project) => <ProjectCard key={project.projectId} project={project} onViewBudget={onViewBudget} />)
        ) : single ? (
          <>
            <Box sx={{ p: "10px 12px", borderRadius: "10px", background: colors.head, fontSize: 14, fontWeight: 600 }}>
              {single.projectName}
            </Box>
            {single.isFixedCost ? (
              <ProjectUtilisationSummary
                hoursLabel={single.hoursLabel}
                individualPct={single.individualPct}
                projectPct={single.projectPct}
                targetPercent={single.targetPercent}
                targetUsedPct={single.targetUsedPct}
                onViewBudget={onViewBudget ? () => onViewBudget(single.projectId) : undefined}
              />
            ) : (
              <HoursOnly project={single} />
            )}
          </>
        ) : null}

        <Box
          sx={{ display: "grid", gridTemplateColumns: onAddAnother ? "repeat(2, minmax(0, 1fr))" : "1fr", gap: 1.5 }}
        >
          <Button
            variant="outlined"
            onClick={onSkip || onClose}
            sx={{ height: 48, borderRadius: "10px", fontWeight: 700, textTransform: "none", borderWidth: "1.5px", color: colors.tealDeep, borderColor: colors.tealDeep, "&:hover": { borderWidth: "1.5px", borderColor: colors.tealDeep, background: colors.tealWash } }}
          >
            Skip
          </Button>
          {onAddAnother && (
            <Button
              variant="contained"
              disableElevation
              onClick={onAddAnother}
              sx={{ height: 48, borderRadius: "10px", fontWeight: 700, textTransform: "none", background: colors.tealDeep, "&:hover": { background: colors.teal } }}
            >
              Add another log
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TimeLogSavedDialog;
